import React, { useEffect, useState } from 'react';
import { FaEdit, FaPlus, FaTrash } from 'react-icons/fa';
import { adminAdvisoryBoardService } from '../services';
import { resolveMediaUrl } from '../services/api';
import { notifyError, notifySuccess } from '../utils/toast';

const defaultForm = {
    name: '',
    position: '',
    status: 'active',
    display_order: 0,
    photo: null,
    social_links: {
        facebook: '',
        instagram: '',
        twitter: '',
        linkedin: '',
        youtube: '',
    },
};

const AdvisoryBoard = () => {
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState(defaultForm);
    const [error, setError] = useState('');

    useEffect(() => {
        fetchMembers();
    }, []);

    const fetchMembers = async () => {
        try {
            const response = await adminAdvisoryBoardService.getAll();
            setMembers(response.data.data || []);
        } catch (err) {
            notifyError('Unable to load advisory board members.');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, files } = e.target;
        if (name.startsWith('social_links.')) {
            const key = name.split('.')[1];
            setFormData((prev) => ({
                ...prev,
                social_links: { ...prev.social_links, [key]: value },
            }));
            return;
        }
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'file' ? files?.[0] || null : value,
        }));
    };

    const resetForm = () => {
        setFormData(defaultForm);
        setEditingId(null);
        setShowForm(false);
        setError('');
    };

    const handleEdit = (member) => {
        setFormData({
            name: member.name || '',
            position: member.position || '',
            status: member.status || 'active',
            display_order: member.display_order || 0,
            photo: null,
            social_links: {
                facebook: member.social_links?.facebook || '',
                instagram: member.social_links?.instagram || '',
                twitter: member.social_links?.twitter || '',
                linkedin: member.social_links?.linkedin || '',
                youtube: member.social_links?.youtube || '',
            },
        });
        setEditingId(member.id);
        setShowForm(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        try {
            if (editingId) {
                await adminAdvisoryBoardService.update(editingId, formData);
                notifySuccess('Advisory board member updated.');
            } else {
                await adminAdvisoryBoardService.create(formData);
                notifySuccess('Advisory board member added.');
            }
            await fetchMembers();
            resetForm();
        } catch (err) {
            const message = err.response?.data?.message || 'Unable to save advisory board member.';
            setError(message);
            notifyError(message);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this advisory board member?')) return;
        try {
            await adminAdvisoryBoardService.delete(id);
            notifySuccess('Advisory board member deleted.');
            await fetchMembers();
        } catch (err) {
            notifyError('Unable to delete advisory board member.');
        }
    };

    return (
        <div>
            <div className="mb-8 flex items-center justify-between">
                <h1 className="text-4xl font-bold text-gray-800">Advisory Board</h1>
                <button
                    onClick={() => setShowForm(!showForm)}
                    className="flex items-center gap-2 rounded-lg bg-emerald-600 px-6 py-2 text-white transition hover:bg-emerald-700"
                >
                    <FaPlus /> Add Member
                </button>
            </div>

            {showForm && (
                <div className="mb-8 rounded-lg bg-white p-8 shadow-lg">
                    <h2 className="mb-6 text-2xl font-bold text-gray-800">{editingId ? 'Edit Member' : 'New Member'}</h2>
                    {error && <div className="mb-4 rounded-lg bg-red-100 p-3 text-red-700">{error}</div>}
                    <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-6 md:grid-cols-2">
                        <div>
                            <label className="mb-2 block font-semibold text-gray-700">Name</label>
                            <input name="name" value={formData.name} onChange={handleInputChange} required className="w-full rounded-lg border border-gray-300 px-4 py-2" />
                        </div>
                        <div>
                            <label className="mb-2 block font-semibold text-gray-700">Position</label>
                            <input name="position" value={formData.position} onChange={handleInputChange} required className="w-full rounded-lg border border-gray-300 px-4 py-2" />
                        </div>
                        <div>
                            <label className="mb-2 block font-semibold text-gray-700">Status</label>
                            <select name="status" value={formData.status} onChange={handleInputChange} className="w-full rounded-lg border border-gray-300 px-4 py-2">
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                            </select>
                        </div>
                        <div>
                            <label className="mb-2 block font-semibold text-gray-700">Display Order</label>
                            <input type="number" min="0" name="display_order" value={formData.display_order} onChange={handleInputChange} className="w-full rounded-lg border border-gray-300 px-4 py-2" />
                        </div>
                        <div className="md:col-span-2">
                            <label className="mb-2 block font-semibold text-gray-700">Photo</label>
                            <input type="file" name="photo" accept="image/*" onChange={handleInputChange} className="w-full rounded-lg border border-gray-300 px-4 py-2" />
                        </div>
                        <div>
                            <label className="mb-2 block font-semibold text-gray-700">Facebook URL</label>
                            <input name="social_links.facebook" value={formData.social_links.facebook} onChange={handleInputChange} className="w-full rounded-lg border border-gray-300 px-4 py-2" />
                        </div>
                        <div>
                            <label className="mb-2 block font-semibold text-gray-700">Instagram URL</label>
                            <input name="social_links.instagram" value={formData.social_links.instagram} onChange={handleInputChange} className="w-full rounded-lg border border-gray-300 px-4 py-2" />
                        </div>
                        <div>
                            <label className="mb-2 block font-semibold text-gray-700">Twitter URL</label>
                            <input name="social_links.twitter" value={formData.social_links.twitter} onChange={handleInputChange} className="w-full rounded-lg border border-gray-300 px-4 py-2" />
                        </div>
                        <div>
                            <label className="mb-2 block font-semibold text-gray-700">LinkedIn URL</label>
                            <input name="social_links.linkedin" value={formData.social_links.linkedin} onChange={handleInputChange} className="w-full rounded-lg border border-gray-300 px-4 py-2" />
                        </div>
                        <div className="md:col-span-2">
                            <label className="mb-2 block font-semibold text-gray-700">YouTube URL</label>
                            <input name="social_links.youtube" value={formData.social_links.youtube} onChange={handleInputChange} className="w-full rounded-lg border border-gray-300 px-4 py-2" />
                        </div>
                        <div className="md:col-span-2 flex gap-4">
                            <button type="submit" className="rounded-lg bg-emerald-600 px-6 py-2 text-white hover:bg-emerald-700">Save</button>
                            <button type="button" onClick={resetForm} className="rounded-lg bg-gray-400 px-6 py-2 text-white hover:bg-gray-500">Cancel</button>
                        </div>
                    </form>
                </div>
            )}

            {loading ? (
                <div className="py-12 text-center">
                    <div className="inline-block h-12 w-12 animate-spin rounded-full border-b-2 border-emerald-600"></div>
                </div>
            ) : (
                <div className="overflow-hidden rounded-lg bg-white shadow-lg">
                    <table className="w-full">
                        <thead className="bg-emerald-600 text-white">
                            <tr>
                                <th className="px-6 py-3 text-left">Photo</th>
                                <th className="px-6 py-3 text-left">Name</th>
                                <th className="px-6 py-3 text-left">Position</th>
                                <th className="px-6 py-3 text-left">Status</th>
                                <th className="px-6 py-3 text-left">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {members.map((member) => (
                                <tr key={member.id} className="border-b hover:bg-gray-50">
                                    <td className="px-6 py-4">
                                        {member.photo ? (
                                            <img src={resolveMediaUrl(member.photo)} alt={member.name} className="h-10 w-10 rounded object-cover" />
                                        ) : (
                                            <span className="text-sm text-gray-500">No Photo</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4">{member.name}</td>
                                    <td className="px-6 py-4">{member.position}</td>
                                    <td className="px-6 py-4">{member.status}</td>
                                    <td className="flex gap-3 px-6 py-4">
                                        <button onClick={() => handleEdit(member)} className="text-emerald-600 hover:text-emerald-800"><FaEdit /></button>
                                        <button onClick={() => handleDelete(member.id)} className="text-red-600 hover:text-red-800"><FaTrash /></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default AdvisoryBoard;
